import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Autocomplete, Box, Button, DataTable, IActionMenuItem, useCommonComponentContext } from "@maysoft/common-component-react";

import { ICodename } from "commons/interfaces";
import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants from "constants/index";
import CodenameService from "services/common/codename.service";
import ModalCreateUpdateCodeName from "./ModalCreateUpdateCodeName";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { BooleanInt } from "constants/enum";
import { setListPathName } from "store/slice/titleRoute.slice";
import { setDataAlert, showLoadingDataTable } from "store/slice/common.slice";


interface IDataFilter {
    pageSize?: number,
    pageNumber?: number,
    totalCount?: number,
    group?: string,
}

const codenameService = new CodenameService();
const defaultGroupRoot = "byHospital";
const rootGroups = [
    "incidentObject",
    "reportType",
    "level",
    "classtify",
    "gender",
    "reportStatus",
    "common",
    "technicalProcessCaused",
    "infectionCaused",
    "medicineCaused",
    "bloodProductCaused",
    "medicalEquipmentCaused",
    "misbehaveCaused",
    "patientIncidentCaused",
    "infrastructureCaused",
    "managementCaused",
    "procedureCaused",
    "staffCaused",
    "patientCaused",
    "environmentCaused",
    "serviceCaused",
    "externalFactorCaused",
    "affectedToOrganization",
    "affectedToPatient",
    "staffTitle",
    "incidentType",
    "causeGroup",
    "levelReport",
    "affectedToPatientCause",
    "managementPatient",
    "equipment",
    "environmentPatient",
    "medical",
    "surgery",
    "crime",
    "byHospital",
    "byHospitalSource",
    "organizationType",
];
export const getGroupName = (key: string): string => {
    const map: Record<string, string> = {
        incidentObject: Strings.REPORT.INCIDENT_OBJECT,
        reportType: Strings.REPORT.REPORT_TYPE,
        gender: Strings.REPORT.GENDER,
        incidentType: Strings.ANALYSIS.INCIDENT_TYPE,
        byHospital: Strings.REPORT.HOSPITAL_CLASS,
        byHospitalSource: Strings.REPORT.SOURCE_REPORT,
        levelReport: Strings.ANALYSIS.LEVEL_REPORT,
        organizationType: Strings.PROJECT_MANAGEMENT.ORGANIZATION_TYPE,
        affectedToOrganization: Strings.ANALYSIS.AFFECTED_TO_ORGANIZATION,
        affectedToPatient: Strings.ANALYSIS.AFFECTED_TO_PATIENT,
        affectedToPatientCause: Strings.ANALYSIS.AFFECTED_TO_PATIENT_CAUSE,
    };
    return map[key] || key;
};
export const codenameGroups: ICodename[] = rootGroups
    .map(item => ({ code: item, name: getGroupName(item) }))
    .filter(codename => codename.code !== codename.name);

const SettingScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { serviceCode, userInfo, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();
    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [idRow, setIdRow] = useState("");

    const [openModal, setOpenModal] = useState(false);
    const [dataRows, setDataRows] = useState<any[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilter>({});

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const group = searchParams.get("group");

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);

        const newReq: IDataFilter = {
            pageSize: size,
            pageNumber: page,
        };

        if (!Helpers.isNullOrEmpty(group)) {
            newReq.group = group;
        } else {
            newReq.group = defaultGroupRoot;
        }

        loadDataSetting(newReq);
    }, [
        pageSize,
        pageNumber,
        group,
        serviceCode,
        userInfo?.userProfile?.organizationId,
    ]);

    const loadDataSetting = async (newReq: IDataFilter) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await codenameService.getPaged({
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
                serviceCode: serviceCode,
                organizationId: userInfo?.userProfile?.organizationId,
                includeCommon: BooleanInt.True,
                group: newReq.group,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));

            const dataRows = result?.items?.map(item => {
                return {
                    ...item,
                    groupName: getGroupName(item.group),
                }
            });
            setDataRows(dataRows);
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilter) => {
        let query: string = `?page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (req.group) {
            query += `&group=${req.group}`;
        }

        navigate(Screens.SETTING + query, { replace: true });
        dispatch(setListPathName({ pathname: Screens.SETTING, query }));
    };

    const dataColumns = useMemo(() => ([
        {
            Header: Strings.SETTING.CODE,
            accessor: "code",
            width: "max-content",
        },
        {
            Header: Strings.SETTING.NAME,
            accessor: "name",
            width: "max-content",
        },
        // {
        //     Header: Strings.SETTING.GROUP,
        //     accessor: "group",
        //     width: "max-content",
        // },
        {
            Header: Strings.SETTING.GROUP_NAME,
            width: "max-content",
            accessor: "groupName",
            Cell: ({ value }: any) => {
                return <>{getGroupName(value)}</>
            },
        },
        {
            Header: Strings.SETTING.TYPE,
            accessor: "organizationId",
            width: "max-content",
            Cell: ({ value }: any) => {
                return <>{((value || "0") === "0") ? Strings.SETTING.DEFAULT : Strings.SETTING.CUSTOM}</>
            },
        },
    ]), []);

    const actionList = useCallback((row: any) => {
        const disabled = row.organizationId === "0" || Helpers.isNullOrEmpty(row.organizationId);
        return [
            !Helpers.isNullOrEmpty(row.organizationId) &&
            {
                disabled,
                key: "edit",
                iconName: "edit",
                title: Strings.COMMON.EDIT,
                callback: (row) => {
                    setIdRow(row.id);
                    setOpenModal(true);
                },
            },
            {
                disabled,
                key: "delete",
                iconName: "delete",
                title: Strings.COMMON.DELETE,
                callback: (row) => {
                    Helpers.showConfirmAlert(Strings.SETTING.CONFIRM_DELETE_CODE_NAME, async () => {
                        try {
                            onShowLoading();
                            await codenameService.delete(row.id);
                            onSuccess(Strings.SETTING.DELETE_CODE_NAME_SUCCESS);
                            await loadDataSetting({ pageNumber: 1, ...dataFilter });
                        } catch (error) {
                            Helpers.isDev() && console.log({ error });
                            onError(Strings.MESSAGE.COMMON_ERROR);
                        } finally {
                            onHideLoading();
                        }
                    });
                },
            },
        ] as IActionMenuItem[];
    }, [dataFilter])

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={Strings.SETTING.TITLE}
            route={[{ title: Strings.SETTING.TITLE, route: "" }]}
        >
            <Box mt={2} mb={2} px={2} display="flex" justifyContent="end">
                <Button startIcon={<AddOutlinedIcon />} onClick={() => { setIdRow(""); setOpenModal(true); }}>
                    {Strings.COMMON.ADD_NEW}
                </Button>
            </Box>

            <Card sx={{ padding: 2 }}>
                <DataTable
                    isShowIndex
                    loading={loadingDataTable}
                    totalCount={dataFilter?.totalCount || 0}
                    rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                    pageNumber={dataFilter?.pageNumber || Constants.PAGE_DEFAULT}
                    onChangePageSize={(value) => {
                        handleFilterData({ ...dataFilter, pageSize: value, });
                    }}
                    filterForm={
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    label={Strings.SETTING.GROUP}
                                    defaultValue={dataFilter?.group}
                                    data={codenameGroups}
                                    placeholder={Strings.SETTING.PLEASE_ENTER_NAME}
                                    onChange={(code: string) => {
                                        setDataFilter(prev => ({
                                            ...prev,
                                            group: code,
                                        }));
                                    }}
                                />
                            </Grid>
                        </Grid>
                    }
                    onCloseFilter={() => {
                        setDataFilter({ ...dataFilter });
                    }}
                    onReset={() => {
                        handleFilterData({ pageNumber: Constants.PAGE_DEFAULT });
                    }}
                    onFilter={() => {
                        handleFilterData({ ...dataFilter, pageNumber: Constants.PAGE_DEFAULT });
                    }}
                    onChangePageNumber={(value) => {
                        handleFilterData({ ...dataFilter, pageNumber: value, });
                    }}
                    table={{ columns: dataColumns as any, rows: dataRows }}

                    actionList={actionList}
                />
            </Card>
            {
                openModal &&
                <ModalCreateUpdateCodeName
                    id={idRow}
                    visible={openModal}
                    group={dataFilter.group}
                    serviceCode={serviceCode}
                    organizationId={userInfo?.userProfile?.organizationId}
                    setVisible={setOpenModal}
                    onCallBack={() => {
                        loadDataSetting({ ...dataFilter });
                    }}
                />
            }
        </DashboardLayout >
    );
};

export default SettingScreen;